import type { Locale } from '@kidshub/contracts';

/**
 * A tidy name for a video, from the title its channel gave it.
 *
 * Titles on children's channels are written for an algorithm rather than for a
 * parent: emoji, "🆕 NEW EPISODES ✨", the channel's name and the year, often
 * in capitals — "😞Mete neden mutsuz?☹️ 🆕YENİ BÖLÜMLER ✨️/ Niloya 2026". The
 * episode is in there, and this finds it: "Mete neden mutsuz?".
 *
 * Only ever a suggestion. The parent sees it, and takes it or not; a heuristic
 * about somebody else's titling habits is not something to apply unasked.
 *
 * Returns null when there is nothing to improve.
 */

/** Promotional phrases, compared in lower case. Stripped only at either end. */
const NOISE_PHRASES = [
  'yeni bölümler',
  'yeni bölüm',
  'yeni oyun',
  'new episodes',
  'new episode',
  'new game',
];

// Pictographs, enclosed letters (🆕), and the selector and joiner that bind them.
const EMOJI = /\p{Extended_Pictographic}|[\u{1F100}-\u{1F1FF}]|\u{FE0F}|\u{200D}/gu;
const SEGMENT_SEPARATOR = /[/|]/;
const EDGE_PUNCTUATION = /^[\s\-–:.,]+|[\s\-–:,]+$/g;

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Removes noise phrases from either end, repeatedly.
 *
 * Not from the middle: in "Mete neden mutsuz? YENİ BÖLÜMLER" the phrase is a
 * label, while in "2026 yılı tüm yeni bölümler bir arada" it is the title.
 */
const trimPhrases = (text: string, locale: Locale): string => {
  let out = text.trim();
  for (let changed = true; changed; ) {
    changed = false;
    for (const phrase of NOISE_PHRASES) {
      const lower = out.toLocaleLowerCase(locale);
      if (lower.startsWith(phrase)) {
        out = out.slice(phrase.length).replace(/^[\s\-–:]+/, '');
        changed = true;
      } else if (lower.endsWith(phrase)) {
        out = out.slice(0, -phrase.length).replace(/[\s\-–:]+$/, '');
        changed = true;
      }
    }
  }
  return out;
};

const cleanSegment = (segment: string, channelNames: string[], locale: Locale): string => {
  let text = segment.replace(/\s{2,}/g, ' ');

  for (const name of channelNames) {
    // The channel's name standing alone. "Niloya'nın gemisi" keeps it.
    const alone = new RegExp(`(^|\\s)${escapeRegExp(name)}(?=\\s|$|[-–])`, 'giu');
    text = text.replace(alone, ' ');
  }

  text = trimPhrases(text.replace(/\s{2,}/g, ' '), locale)
    // "Benekli 'nin" — a suffix separated from its word.
    .replace(/\s+'(?=\p{L})/gu, "'")
    .replace(/\s{2,}/g, ' ')
    .replace(EDGE_PUNCTUATION, '')
    .trim();

  // A year left on its own was "Niloya 2026"; inside a title it stays.
  return /^\d{4}$/.test(text) ? '' : text;
};

/** "OKUL VE YENİ ARKADAŞLAR" → "Okul ve yeni arkadaşlar", with Turkish casing. */
const unshout = (text: string, locale: Locale): string => {
  const letters = text.replace(/[^\p{L}]/gu, '');
  if (letters.length <= 3 || letters !== letters.toLocaleUpperCase(locale)) {
    return text;
  }
  const lower = text.toLocaleLowerCase(locale);
  return lower.charAt(0).toLocaleUpperCase(locale) + lower.slice(1);
};

export const suggestTitle = (
  raw: string,
  channelNames: string[],
  locale: Locale,
): string | null => {
  const names = channelNames.flatMap((name) => [name, name.toLocaleUpperCase(locale)]);
  const first = raw
    .replace(EMOJI, ' ')
    .split(SEGMENT_SEPARATOR)
    .map((segment) => cleanSegment(segment, names, locale))
    .find((segment) => segment !== '');

  if (first === undefined) {
    return null;
  }

  const suggestion = unshout(first, locale);
  return suggestion === raw.trim() ? null : suggestion;
};
